import { module } from 'modujs';

import { models, SUPPORT_WEBGL } from '../utils/environment'
import { gltfLoader, disposeRenderer } from '../utils/3d'

import { AmbientLight, Scene, PerspectiveCamera, Clock, WebGLRenderer, PCFSoftShadowMap, SRGBColorSpace, AxesHelper, DoubleSide, Group, AnimationMixer, AnimationClip } from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
// import { USDZExporter } from 'three/examples/jsm/exporters/USDZExporter.js';

export default class extends module {
    constructor(m) {
        super(m);

        this.canvas = this.el;

        this.autoplayAnimations = true;
        this.defaultAnimation = false;

        // models = [];
        this.currentModel = this.getData('file') ? this.getData('file') : false

        this.resize();
        this.resizeBind = this.resize.bind(this);
        window.addEventListener('resize', this.resizeBind);
    }

    init() {
        if(!SUPPORT_WEBGL) return;

        this.initRenderer();
        this.initScene();
        this.initLoaders();

        this.render();

        this.currentModel && this.loadModel()
    }

    initRenderer() {
        this.renderer = new WebGLRenderer({
            canvas: this.canvas,
            alpha: true,
            antialias: true,
            preserveDrawingBuffer: true // for screenshots
        })

        this.renderer.shadowMap.enabled = true
        this.renderer.shadowMap.type = PCFSoftShadowMap

        this.renderer.outputEncoding = SRGBColorSpace;
        // renderer.toneMapping = THREE.ACESFilmicToneMapping;
        // renderer.toneMappingExposure = 1;

        this.renderer.setSize(this.sizes.width, this.sizes.height)
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

        this.clock = new Clock()
        this.previousTime = 0
    }

    initScene() {
        this.scene = new Scene();

        // const axesHelper = new AxesHelper( 1 );
        // this.scene.add( axesHelper );

        /**
         * Lights
         */
        const ambientLight = new AmbientLight(0xffffff, 6)
        this.scene.add(ambientLight)

        // Base camera
        this.camera = new PerspectiveCamera(30, this.sizes.width / this.sizes.height, 0.01,100)
        this.camera.position.set(1,1,1)
        this.scene.add(this.camera)

        // Controls
        this.controls = new OrbitControls(this.camera, this.canvas)
        this.controls.target.set(0, .25, 0)
        this.controls.enableDamping = true

        // Wrapper
        this.wrapper = new Group();
        this.wrapper.position.set(0,0,0);
        this.scene.add(this.wrapper)
    }

    initLoaders() {
        this.supportsQuickLook = false
        const a = document.createElement("a");
        if (a.relList.supports("ar")) {
            this.supportsQuickLook = true;
        }
    }

    resize() {
        this.BCR = this.el.getBoundingClientRect()

        this.sizes = {
            width: this.BCR.width,
            height: this.BCR.height
        }

        this.renderer && this.renderer.setSize(this.sizes.width, this.sizes.height);

        // this.renderer && console.log(this.renderer);

        if(this.camera) {
            // Update camera
            this.camera.aspect = this.sizes.width / this.sizes.height
            this.camera.updateProjectionMatrix()
        }

        // Update renderer
        if(this.renderer) {
            this.renderer.setSize(this.sizes.width, this.sizes.height)
            this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
        }
    }

    render() {
        const elapsedTime = this.clock.getElapsedTime()
        this.deltaTime = elapsedTime - this.previousTime
        this.previousTime = elapsedTime

        // Update controls
        this.controls.update()

        // Render
        this.renderer.render(this.scene, this.camera)

        if(this.currentModel && models[this.currentModel] && models[this.currentModel].mixer) models[this.currentModel].mixer.setTime(elapsedTime)

        // Call tick again on the next frame
        this.raf = window.requestAnimationFrame(this.render.bind(this))
    }


    loadModel(src = this.currentModel) {
        return new Promise((resolve, reject) => {
            if(!models[src]) {
                models[src] = {
                    glb: null,
                    usdz: null,
                    isLoaded: false,
                    onLoad: () => {}
                }
            } else {
                resolve()
                return;
            }

            gltfLoader.load(src, gltf => {
                models[src].glb = gltf
                models[src].isLoaded = true

                // Orientation in USDZ is broken for now because of animations, we may need to manually generate UDSZ files upfront instead
                // let scope = this;
                // async function generateUsdz() {
                //     const exporter = new USDZExporter();

                //     const arraybuffer = await exporter.parse( gltf.scene );
                //     const blob = new Blob( [ arraybuffer ], { type: 'application/octet-stream' } );
                //     models[src].usdz = URL.createObjectURL( blob );

                //     if(scope.currentModel == src) {
                //         // scope.quickLook.href = scope.models[src].usdz;
                //         scope.el.dispatchEvent(new CustomEvent('usdzReady', { detail: { usdz: models[src].usdz }}))
                //     }
                // }
                // this.supportsQuickLook && generateUsdz()

                if(gltf.animations.length) {
                    models[src].mixer = new AnimationMixer( gltf.scene );
                    gltf.scene.traverse(function(obj) { obj.frustumCulled = false; });

                    models[src].animations = []
                    for(let i = 0; i < gltf.animations.length; i++) {
                        let name = gltf.animations[i].name;
                        if(models[src].animations[gltf.animations[i].name]) name+=i
                        models[src].animations[name] = models[src].mixer.clipAction(gltf.animations[i]);
                    }
                }

                gltf.scene.scale.set(1,1,1)
                gltf.scene.visible = src === this.currentModel

                this.wrapper.add(gltf.scene)

                models[src].onLoad()

                resolve()
            })
        });
    }

    setModel(src) {
        if(!SUPPORT_WEBGL) return;

        // console.trace('[ModelViewer:setModel()]',src);
        window.cancelAnimationFrame(this.raf)

        this.wrapper.clear()
        this.el.dispatchEvent(new CustomEvent('hideUsdz'))

        this.currentModel = src
        let prevAnimationKey;

        const handleAnimation = () => {
            if(this.currentModel !== src) return

            for(let id of Object.keys(models[this.currentModel].animations)) {
                if(models[src].animations[id].isRunning()) prevAnimationKey = id;
                models[src].animations[id].stop()
            }

            if(this.autoplayAnimations) {
                if(this.defaultAnimation && models[src].animations[this.defaultAnimation]) {
                    models[src].animations[this.defaultAnimation].play()
                } else {

                    // Pick an animation
                    let newAnimationKey;
                    let rand = Math.random();
                    while (
                        !newAnimationKey // No key is set
                        || (newAnimationKey == 'idle-1' && rand > 0.33) // `idle-1` allowed only 33% of the time
                        || (newAnimationKey == 'walking' && rand > 0.5) // `walking` allowed only 50% of the time
                        || newAnimationKey == 'idle-2' // Disable `idle-2` entirely
                        || newAnimationKey == prevAnimationKey // Prevent newAnimation to be the same as prevAnimation
                    ) {
                        newAnimationKey = Object.keys(models[src].animations)[Math.floor(Math.random()*Object.keys(models[src].animations).length)]
                        rand = Math.random();
                    }

                    models[src].animations[newAnimationKey].play()
                }
            }
        }

        const showModel = () => {
            if(this.currentModel === src) {
                handleAnimation();
                models[src].glb.scene.visible = true
                this.supportsQuickLook && models[src].usdz && this.el.dispatchEvent(new CustomEvent('usdzReady', { detail: { usdz: models[src].usdz }}))

                this.render()
            }
        }

        if(this.currentModel == false) {
            this.render()
            return;
        } else if(!models[src]) {
            this.loadModel(src)
            models[src].onLoad = showModel
        } else if(!models[src].isLoaded) {
            models[src].onLoad = () => {
                if(!this.wrapper.children.includes(models[src].glb.scene)) {
                    this.wrapper.add(models[src].glb.scene)
                }
                showModel()
            }
        } else if(!this.wrapper.children.includes(models[src].glb.scene)) {
            this.wrapper.add(models[src].glb.scene)
            showModel()
        } else {
            showModel()
        }
    }

    setDefaultAnimation(value) {
        this.defaultAnimation = value;
    }

    destroy() {
        window.removeEventListener('resize', this.resizeBind);
        window.cancelAnimationFrame(this.raf)
        disposeRenderer(this.renderer, this.scene, { controls: this.controls });
        this.scene = this.renderer = this.controls = null;
    }
}
